import React, { useState, useEffect, Suspense, startTransition } from 'react';
import { checkAndHandleNewVersion } from './utils/versionCheck';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Usuario, RolUsuario, Notification } from './types';
import { Helmet } from 'react-helmet-async';
import Loader from './components/ui/Loader';
import { actualizarAvisosExpirados } from './services/avisoService';
import { supabase } from './services/supabaseClient';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazyWithRetry } from './utils/lazyWithRetry';
import ErrorBoundary from './components/ui/ErrorBoundary';

// Context providers must be imported directly (not lazy) to avoid suspense on synchronous access
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import { ConfirmProvider } from './contexts/ConfirmContext';
import { CartProvider } from './contexts/CartContext';
import { CurrencyProvider } from './contexts/CurrencyContext';

// Lazy load all route components for code splitting
const Auth = lazyWithRetry(() => import('./components/Auth'));
const ResetPassword = lazyWithRetry(() => import('./components/ResetPassword'));
const Layout = lazyWithRetry(() => import('./components/Layout'));
const ScrollToTop = lazyWithRetry(() => import('./components/ScrollToTop'));

const guestUser: Usuario = {
    id_usuario: 0,
    nombre: "Invitado",
    email: "invitado@crm.com",
    rol: RolUsuario.Cliente,
    telefono: "",
    whatsapp: "",
    direccion: "",
    provincia: "",
    fecha_registro: new Date().toISOString(),
    verificado: false,
};



const Dashboard = lazyWithRetry(() => import('./components/Dashboard'));
const GestionAvisos = lazyWithRetry(() => import('./components/GestionAvisos'));
const GestionPlanes = lazyWithRetry(() => import('./components/GestionPlanes'));
const GestionPlanesTienda = lazyWithRetry(() => import('./components/GestionPlanesTienda'));
const GestionClientes = lazyWithRetry(() => import('./components/GestionClientes'));
const GestionFraude = lazyWithRetry(() => import('./components/GestionFraude'));
const GestionAfiliados = lazyWithRetry(() => import('./components/GestionAfiliados'));
const GestionCategorias = lazyWithRetry(() => import('./components/GestionCategorias'));
const GestionTiendas = lazyWithRetry(() => import('./components/GestionTiendas'));
const ClienteDetalle = lazyWithRetry(() => import('./components/ClienteDetalle'));
const AvisosClasificados = lazyWithRetry(() => import('./components/AvisosClasificados'));
const MisAvisos = lazyWithRetry(() => import('./components/MisAvisos'));
const MisAlertas = lazyWithRetry(() => import('./components/MisAlertas'));
const ChatPanel = lazyWithRetry(() => import('./components/ChatPanel'));
const MiPerfilView = lazyWithRetry(() => import('./components/MiPerfilView'));
const MisFavoritosView = lazyWithRetry(() => import('./components/MisFavoritosView'));
const MisCompras = lazyWithRetry(() => import('./components/MisCompras'));
const AvisoDetalle = lazyWithRetry(() => import('./components/AvisoDetalle'));
const ListadoTiendas = lazyWithRetry(() => import('./components/ListadoTiendas'));
const Tienda = lazyWithRetry(() => import('./components/Tienda'));
const MiTienda = lazyWithRetry(() => import('./components/MiTienda'));
const AfiliadosDashboard = lazyWithRetry(() => import('./components/Afiliados/DashboardAfiliado'));
const AffiliateProgramInfo = lazyWithRetry(() => import('./components/Afiliados/AffiliateProgramInfo'));
const GestionProductosTienda = lazyWithRetry(() => import('./components/GestionProductosTienda'));
const PedidosTienda = lazyWithRetry(() => import('./components/PedidosTienda'));
const ConfiguracionTienda = lazyWithRetry(() => import('./components/ConfiguracionTienda'));
const EstadisticasTienda = lazyWithRetry(() => import('./components/EstadisticasTienda'));
const CuponesTienda = lazyWithRetry(() => import('./components/tienda/GestionCuponesTienda'));
const CuponesTiendaWrapper = lazyWithRetry(() => import('./components/CuponesTiendaWrapper'));
const GestionBanners = lazyWithRetry(() => import('./components/GestionBanners'));
const Carrito = lazyWithRetry(() => import('./components/Carrito'));
const Checkout = lazyWithRetry(() => import('./components/Checkout'));
const Legal = lazyWithRetry(() => import('./components/Legal'));
const Sitemap = lazyWithRetry(() => import('./components/Sitemap'));
const Ayuda = lazyWithRetry(() => import('./components/Ayuda'));
const AnalyticsDashboard = lazyWithRetry(() => import('./components/Analytics/AnalyticsDashboard'));
const MarketingPanel = lazyWithRetry(() => import('./components/Admin/MarketingPanel'));
const HomeConfigurator = lazyWithRetry(() => import('./components/Admin/HomeConfigurator'));
const LandingDSF = lazyWithRetry(() => import('./components/LandingDSF'));
const Home = lazyWithRetry(() => import('./components/Home/Home'));
const StoreAdminLayout = lazyWithRetry(() => import('./components/layouts/StoreAdminLayout'));
const AyudaTienda = lazyWithRetry(() => import('./components/AyudaTienda'));
const VirtualAssistant = lazyWithRetry(() => import('./components/VirtualAssistant'));
const PushNotificationManager = lazyWithRetry(() => import('./components/PushNotificationManager'));
const VisitsTracker = lazyWithRetry(() => import('./components/Analytics/VisitsTracker'));
const VersionChecker = lazyWithRetry(() => import('./components/VersionChecker'));
const EmailTest = lazyWithRetry(() => import('./components/EmailTest'));
const PWAInstallPrompt = lazyWithRetry(() => import('./components/PWAInstallPrompt'));
const AuditLogsViewer = lazyWithRetry(() => import('./components/Admin/AuditLogsViewer'));

// Create a client for React Query with optimized settings for classified ads
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 5 * 60 * 1000, // Data is fresh for 5 minutes
            gcTime: 10 * 60 * 1000, // Garbage collection time (was cacheTime in v4)
            refetchOnWindowFocus: false, // Don't refetch when user returns to tab
            retry: 1, // Only retry failed queries once
        },
    },
});

// AuthListener component to handle global auth events inside Router context
const AuthListener = ({ onLoginSuccess, currentUserId }: { onLoginSuccess: (user: Usuario) => void; currentUserId?: number }) => {
    const navigate = useNavigate();

    useEffect(() => {
        const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
            if (event === "PASSWORD_RECOVERY") {
                navigate('/reset-password');
            } else if (event === "SIGNED_IN" && session?.user) {
                // Don't redirect if user is on the password reset page — let them change their password first
                if (window.location.pathname === '/reset-password') return;

                // Check if we already have this user in state to avoid redundant profile fetches
                if (currentUserId && currentUserId > 0) return;

                // Handle OAuth or manual sign-in where we don't have the profile yet
                try {
                    const { data: userData, error: userError } = await supabase
                        .from('usuarios')
                        .select('*')
                        .eq('email', session.user.email)
                        .maybeSingle();

                    if (!userError && userData) {
                        onLoginSuccess(userData as Usuario);
                    }
                } catch (err) {
                    console.error("Error fetching user profile after SIGNED_IN:", err);
                }
            }
        });
        return () => {
            authListener.subscription.unsubscribe();
        };
    }, [navigate, onLoginSuccess, currentUserId]);

    return null;
};

const App: React.FC = () => {
    // Initial state is null (loading/logged out) until session is restored
    const [currentUser, setCurrentUser] = useState<Usuario | null>(null);
    // Notifications now managed by NotificationCenter component

    // Check for expired avisos every 5 minutes
    useEffect(() => {
        // Restore session - only if user is actually authenticated (id_usuario > 0)
        const savedUser = localStorage.getItem('crm_user');
        if (savedUser) {
            try {
                const user = JSON.parse(savedUser);
                // Only restore session if it's a real user (not guest)
                if (user && user.id_usuario && user.id_usuario > 0) {
                    setCurrentUser(user);
                } else {
                    // Default to guest if no valid session
                    setCurrentUser(guestUser);
                }
            } catch (e) {
                console.error("Error parsing saved session", e);
                setCurrentUser(guestUser);
            }
        } else {
            // First time entry or no session: Start as guest
            setCurrentUser(guestUser);
        }
    }, []);

    // Check for expired avisos every 5 minutes (Admins only)
    useEffect(() => {
        if (currentUser?.rol === RolUsuario.Administrador) {
            // Run immediately
            actualizarAvisosExpirados();

            // Then run every 5 minutes
            const interval = setInterval(() => {
                actualizarAvisosExpirados();
            }, 5 * 60 * 1000); // 5 minutes in milliseconds

            return () => clearInterval(interval);
        }
    }, [currentUser]);

    // Capture Affiliate Reference
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const refCode = params.get('ref');
        if (refCode) {
            import('./services/affiliateService').then(module => {
                module.registrarVisitaAfiliado(refCode);
            });
        }
    }, []);

    // Version Update and Service Worker Logic
    useEffect(() => {
        // Redundant manual polling removed ( handled by VersionChecker component )

        // Service Worker Controller Change (Force reload when SW updates)
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('controllerchange', () => {
                // Service Worker controller changed, reloading...
                window.location.reload();
            });
        }
    }, []);

    // Old notification system removed - now using NotificationCenter component with real-time Supabase updates

    const handleLoginSuccess = (user: Usuario) => {
        // Save user session
        localStorage.setItem('crm_user', JSON.stringify(user));

        // Force redirect to classifieds list regardless of previous location
        window.history.pushState({}, '', '/clasificados');
        // Use startTransition to avoid suspense errors during lazy component loading
        startTransition(() => {
            setCurrentUser(user);
        });
    };

    const handleUserUpdate = (user: Usuario) => {
        // Save user session without redirect
        localStorage.setItem('crm_user', JSON.stringify(user));
        startTransition(() => {
            setCurrentUser(user);
        });
    };

    const handleLogout = () => {
        console.trace("🚪 Logout triggered from App.tsx. Stack trace:");
        // 1. Clear current user state
        startTransition(() => {
            setCurrentUser(null);
        });

        // Notifications now managed by NotificationCenter

        // 3. Clear React Query cache completely
        queryClient.clear();

        // 4. Clear localStorage but PRESERVE shopping carts
        const keysToKeep = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('shopping_cart_')) {
                keysToKeep.push({ key: key, value: localStorage.getItem(key) });
            }
        }

        localStorage.clear();
        // Explicitly ensure crm_user is gone (though clear() does it, this is for clarity/safety if we change logic)
        localStorage.removeItem('crm_user');

        // Restore cart keys
        keysToKeep.forEach(item => {
            if (item.key && item.value) {
                localStorage.setItem(item.key, item.value);
            }
        });

        // 5. Clear sessionStorage
        sessionStorage.clear();

        // 6. Invalidate all queries to force fresh data on next login
        queryClient.invalidateQueries();

        // Cache cleared on logout, carts preserved
    };

    if (!currentUser) {
        return (
            <ThemeProvider>
                <ToastProvider>
                    <ConfirmProvider>
                        <BrowserRouter>
                            <AuthListener onLoginSuccess={handleLoginSuccess} currentUserId={currentUser?.id_usuario} />
                            <ScrollToTop />
                            <ErrorBoundary>
                                <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader text="Cargando..." /></div>}>
                                    <Routes>
                                        {/* Public routes */}
                                        <Route path="/legal" element={<Legal />} />
                                        <Route path="/sitemap" element={<Sitemap />} />
                                        <Route path="/reset-password" element={<ResetPassword />} />
                                        <Route path="/email-test" element={<EmailTest />} />
                                        {/* Default to Auth for all other routes */}
                                        <Route path="*" element={<Auth onLoginSuccess={handleLoginSuccess} />} />
                                    </Routes>
                                </Suspense>
                            </ErrorBoundary>
                        </BrowserRouter>
                    </ConfirmProvider>
                </ToastProvider>
            </ThemeProvider>
        );
    }

    const AdminRoute = ({ children }: { children: React.ReactElement }) => {
        if (!currentUser || currentUser.rol !== RolUsuario.Administrador) {
            return <Navigate to="/clasificados" replace />;
        }
        return children;
    };

    // Note: currentUser is now effectively always present (either real user or guestUser)
    return (
        <QueryClientProvider client={queryClient}>
            <Helmet
                titleTemplate="%s | PublicaloGratis"
                defaultTitle="PublicaloGratis - Avisos Clasificados en Argentina"
            >
                <meta name="description" content="Publica tus avisos clasificados gratis en Argentina. Encuentra lo que buscas o vende lo que ya no usas." />
            </Helmet>
            <ThemeProvider>
                <ToastProvider>
                    <ConfirmProvider>
                        <CurrencyProvider>
                            <CartProvider currentUser={currentUser || guestUser}>
                                <BrowserRouter>
                                    <AuthListener onLoginSuccess={handleLoginSuccess} currentUserId={currentUser?.id_usuario} />
                                    <ScrollToTop />
                                    <ErrorBoundary>
                                        <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader text="Cargando..." /></div>}>
                                            <Routes>
                                                {/* Public routes available even without "Layout" wrapper if needed, 
                                                    but Layout usually provides the shell */}
                                                <Route path="/reset-password" element={<ResetPassword />} />
                                                <Route path="/email-test" element={<EmailTest />} />

                                                <Route path="/" element={<Navigate to="/clasificados" replace />} />

                                                <Route element={
                                                    <Layout
                                                        currentUser={currentUser || guestUser}
                                                        onLogout={handleLogout}
                                                    />
                                                }>
                                                    {/* Secondary home or marketplace view */}
                                                    <Route path="home" element={<Home currentUser={currentUser || guestUser} />} />

                                                    <Route path="login" element={<Auth onLoginSuccess={handleLoginSuccess} />} />

                                                    <Route path="dashboard" element={
                                                        <Dashboard currentUser={currentUser || guestUser} />
                                                    } />
                                                    <Route path="admin/fraude" element={
                                                        <AdminRoute>
                                                            <GestionFraude currentUser={currentUser || guestUser} />
                                                        </AdminRoute>
                                                    } />
                                                    <Route path="admin/afiliados" element={
                                                        <AdminRoute>
                                                            <GestionAfiliados currentUser={currentUser || guestUser} />
                                                        </AdminRoute>
                                                    } />
                                                    <Route path="analytics" element={
                                                        <AdminRoute>
                                                            <AnalyticsDashboard currentUser={currentUser || guestUser} />
                                                        </AdminRoute>
                                                    } />
                                                    <Route path="admin/marketing" element={
                                                        <AdminRoute>
                                                            <MarketingPanel />
                                                        </AdminRoute>
                                                    } />
                                                    <Route path="admin/home" element={
                                                        <AdminRoute>
                                                            <HomeConfigurator />
                                                        </AdminRoute>
                                                    } />

                                                    {/* NEW HOME PAGE */}
                                                    <Route path="home" element={<Home currentUser={currentUser || guestUser} />} />

                                                    {/* SEARCH PAGE (Previous Home) */}
                                                    <Route path="clasificados" element={<AvisosClasificados currentUser={currentUser || guestUser} />} />
                                                    <Route path="mi-perfil" element={
                                                        currentUser && currentUser.id_usuario > 0 ? (
                                                            <MiPerfilView currentUser={currentUser} onUpdateUser={handleUserUpdate} />
                                                        ) : (
                                                            <Navigate to="/login" replace />
                                                        )
                                                    } />
                                                    <Route path="publicar" element={<Navigate to="/mis-avisos?action=new" replace />} />
                                                    <Route path="mis-avisos" element={
                                                        currentUser && currentUser.id_usuario > 0 ? (
                                                            <MisAvisos currentUser={currentUser} />
                                                        ) : (
                                                            <Navigate to="/login" replace />
                                                        )
                                                    } />
                                                    <Route path="mis-alertas" element={
                                                        currentUser && currentUser.id_usuario > 0 ? (
                                                            <MisAlertas currentUser={currentUser} />
                                                        ) : (
                                                            <Navigate to="/login" replace />
                                                        )
                                                    } />
                                                    <Route path="mensajes" element={
                                                        currentUser && currentUser.id_usuario > 0 ? (
                                                            <ChatPanel currentUser={currentUser} />
                                                        ) : (
                                                            <Navigate to="/login" replace />
                                                        )
                                                    } />
                                                    <Route path="favoritos" element={<MisFavoritosView currentUser={currentUser || guestUser} />} />
                                                    <Route path="mis-compras" element={
                                                        currentUser && currentUser.id_usuario > 0 ? (
                                                            <MisCompras currentUser={currentUser} />
                                                        ) : (
                                                            <Navigate to="/login" replace />
                                                        )
                                                    } />
                                                    <Route path="afiliados" element={
                                                        currentUser && currentUser.id_usuario > 0 ? (
                                                            <AfiliadosDashboard currentUser={currentUser} />
                                                        ) : (
                                                            <Navigate to="/login" replace />
                                                        )
                                                    } />
                                                    <Route path="programa-afiliados" element={<AffiliateProgramInfo />} />
                                                    <Route path="gestion-avisos" element={
                                                        <AdminRoute>
                                                            <GestionAvisos currentUser={currentUser || guestUser} onNavigateToAvisoDetalle={() => { }} />
                                                        </AdminRoute>
                                                    } />
                                                    <Route path="gestion-tiendas" element={
                                                        <AdminRoute>
                                                            <GestionTiendas currentUser={currentUser || guestUser} />
                                                        </AdminRoute>
                                                    } />
                                                    <Route path="admin/banners" element={
                                                        <AdminRoute>
                                                            <GestionBanners currentUser={currentUser || guestUser} />
                                                        </AdminRoute>
                                                    } />
                                                    <Route path="admin/auditoria" element={
                                                        <AdminRoute>
                                                            <AuditLogsViewer currentUser={currentUser || guestUser} />
                                                        </AdminRoute>
                                                    } />
                                                    <Route path="planes" element={<GestionPlanes currentUser={currentUser || guestUser} />} />
                                                    <Route path="planes-tienda" element={<GestionPlanesTienda currentUser={currentUser || guestUser} />} />
                                                    <Route path="clientes" element={<GestionClientes currentUser={currentUser || guestUser} onRegisterSuccess={handleLoginSuccess} />} />
                                                    <Route path="categorias" element={
                                                        <AdminRoute>
                                                            <GestionCategorias currentUser={currentUser || guestUser} />
                                                        </AdminRoute>
                                                    } />

                                                    <Route path="aviso/:id" element={<AvisoDetalle avisoId={0} currentUser={currentUser || guestUser} />} />
                                                    <Route path="cliente/:id" element={<ClienteDetalle clienteId={0} onBack={() => window.history.back()} currentUser={currentUser || guestUser} />} />

                                                    {/* E-commerce Routes */}
                                                    <Route path="tiendas" element={<ListadoTiendas />} />
                                                    <Route path="tienda/:slug" element={<Tienda currentUser={currentUser || guestUser} />} />

                                                    {/* Store Admin Routes */}
                                                    <Route element={<StoreAdminLayout currentUser={currentUser || guestUser} />}>
                                                        <Route path="mi-tienda" element={
                                                            currentUser && (currentUser.rol === RolUsuario.ClienteEmpresa || currentUser.rol === RolUsuario.Administrador) ? (
                                                                <MiTienda currentUser={currentUser} />
                                                            ) : (
                                                                <Navigate to="/login" replace />
                                                            )
                                                        } />
                                                        {/* Adding protection to other store admin routes */}
                                                        <Route path="mi-tienda/productos" element={currentUser && currentUser.id_usuario > 0 ? <GestionProductosTienda currentUser={currentUser} /> : <Navigate to="/login" />} />
                                                        <Route path="mi-tienda/pedidos" element={currentUser && currentUser.id_usuario > 0 ? <PedidosTienda currentUser={currentUser} /> : <Navigate to="/login" />} />
                                                        <Route path="mi-tienda/configuracion" element={currentUser && currentUser.id_usuario > 0 ? <ConfiguracionTienda currentUser={currentUser} /> : <Navigate to="/login" />} />
                                                        <Route path="mi-tienda/estadisticas" element={currentUser?.rol === RolUsuario.ClienteEmpresa ? <EstadisticasTienda currentUser={currentUser} /> : <Navigate to="/clasificados" />} />
                                                        <Route path="mi-tienda/cupones" element={currentUser && currentUser.id_usuario > 0 ? <CuponesTiendaWrapper currentUser={currentUser} /> : <Navigate to="/login" />} />
                                                        <Route path="mi-tienda/ayuda" element={currentUser && currentUser.id_usuario > 0 ? <AyudaTienda currentUser={currentUser} /> : <Navigate to="/login" />} />
                                                    </Route>

                                                    {/* New E-commerce Routes */}
                                                    <Route path="carrito" element={<Carrito />} />
                                                    <Route path="checkout" element={currentUser && currentUser.id_usuario > 0 ? <Checkout currentUser={currentUser} /> : <Navigate to="/login" />} />

                                                    {/* Legal & Sitemap */}
                                                    <Route path="legal" element={<Legal />} />
                                                    <Route path="sitemap" element={<Sitemap />} />
                                                    <Route path="ayuda" element={<Ayuda />} />

                                                    {/* Catch-all route */}
                                                    <Route path="*" element={<Navigate to="/clasificados" replace />} />
                                                </Route>
                                            </Routes>
                                        </Suspense>
                                    </ErrorBoundary>
                                    <VirtualAssistant currentUser={currentUser || guestUser} />
                                    <PushNotificationManager />
                                    <VisitsTracker />
                                    <VersionChecker />
                                    <PWAInstallPrompt />
                                </BrowserRouter>
                            </CartProvider>
                        </CurrencyProvider>
                    </ConfirmProvider>
                </ToastProvider>
            </ThemeProvider>
        </QueryClientProvider>
    );
};

export default App;